import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdSalesPromotionDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useTaskTablesContext } from '../../context';

export const PromotionFilter: FC = () => {
  const [t] = useTranslation(['backend', 'common']);
  const filters = useTaskTablesContext('filters$');
  const { updateFilter, promotionPrefix, setPromotionPrefix, promotionRequest } = useTaskTablesContext();

  const options = useSubscribe(
    promotionRequest.data$,
    (response) => response?.data.items.map((item) => ({ label: item.name, value: item.id })) ?? [],
  );
  const total = useSubscribe(promotionRequest.data$, (response) => response?.data.total ?? 0);
  const loading = useSubscribe(promotionRequest.pending$);

  const handleChange = useCallbackRef((option: Option<IdSalesPromotionDto> | null) => {
    updateFilter('promotion', option);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: promotionPrefix, setFilter: setPromotionPrefix, total }),
    [promotionPrefix, setPromotionPrefix, total],
  );

  return (
    <Filter<IdSalesPromotionDto>
      label={t('common:PROMOTION')}
      options={options}
      selected={filters.promotion}
      onChange={handleChange}
      loading={loading}
      serverSideFiltering={serverSideFiltering}
      isClearable
      dataTestId='promotion-filter'
    />
  );
};
